import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  SendHorizontal, Users, RefreshCw, Loader2, Plus, Pencil, Trash2, Save, X,
  Clock, Globe, MessageSquare, History, CheckCircle2, XCircle, Ban,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useMasterData } from '../contexts/MasterDataContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { API } from '../utils/apiConfig';

const TZ_OPTIONS = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'UTC',
  'Europe/London', 'America/New_York',
];

const VENUE_CODES = ['ISF', 'MSC', 'BHY'];

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Something went wrong';
  if (typeof d === 'string') return d;
  return d.message || JSON.stringify(d);
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { hour12: false }); }
  catch { return iso; }
};

export default function DailySalesReportPage() {
  const { getVenueNames } = useMasterData();
  const venueNames = getVenueNames?.() || [];

  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sending, setSending] = useState(false);

  const [showRecipientForm, setShowRecipientForm] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [recipientForm, setRecipientForm] = useState({ name: '', mobile: '', is_active: true });
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [expandedLog, setExpandedLog] = useState(null);

  // ---------- fetch ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, r, h] = await Promise.all([
        axios.get(`${API}/daily-sales-report/settings`),
        axios.get(`${API}/daily-sales-report/preview`),
        axios.get(`${API}/daily-sales-report/recipients`),
        axios.get(`${API}/daily-sales-report/history?limit=20`),
      ]);
      setSettings(s.data);
      setPreview(p.data);
      setRecipients(r.data || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---------- settings ----------
  const patchSettings = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const patchVenueMap = (code, name) => setSettings((s) => ({
    ...s, venue_map: { ...(s.venue_map || {}), [code]: name },
  }));

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/daily-sales-report/settings`, {
        template_name: settings.template_name,
        send_time: settings.send_time,
        timezone: settings.timezone,
        enabled: settings.enabled,
        venue_map: settings.venue_map,
      });
      setSettings(res.data);
      // refresh preview because venue_map may change figures
      const p = await axios.get(`${API}/daily-sales-report/preview`);
      setPreview(p.data);
      toast.success('Report settings saved');
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSavingSettings(false); }
  };

  // ---------- send now ----------
  const sendNow = async () => {
    if (!window.confirm('Send the Daily Sales Report to all active recipients now?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/daily-sales-report/send-now`);
      if (res.data.sent) {
        const ok = res.data.log?.success_count || 0;
        const fail = res.data.log?.failure_count || 0;
        if (ok > 0 && fail === 0) toast.success(`Sent to ${ok} recipient${ok > 1 ? 's' : ''}`);
        else if (ok > 0) toast.warning(`Sent to ${ok}, failed on ${fail}`);
        else toast.error(`WABA delivery failed on all ${fail} recipient${fail > 1 ? 's' : ''}`);
      } else {
        toast.warning(res.data.reason || 'Skipped');
      }
      // refresh history
      const h = await axios.get(`${API}/daily-sales-report/history?limit=20`);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSending(false); }
  };

  // ---------- recipient CRUD ----------
  const openNewRecipient = () => {
    setEditingRecipient(null);
    setRecipientForm({ name: '', mobile: '', is_active: true });
    setShowRecipientForm(true);
  };
  const openEditRecipient = (r) => {
    setEditingRecipient(r);
    setRecipientForm({ name: r.name, mobile: r.mobile, is_active: r.is_active });
    setShowRecipientForm(true);
  };
  const saveRecipient = async () => {
    const mobile = (recipientForm.mobile || '').replace(/\D/g, '').slice(-10);
    if (!recipientForm.name.trim()) { toast.error('Name is required'); return; }
    if (mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile'); return; }
    setSavingRecipient(true);
    try {
      if (editingRecipient) {
        const res = await axios.put(`${API}/daily-sales-report/recipients/${editingRecipient.id}`, {
          name: recipientForm.name.trim(), mobile, is_active: recipientForm.is_active,
        });
        setRecipients((prev) => prev.map((x) => (x.id === res.data.id ? res.data : x)));
        toast.success('Recipient updated');
      } else {
        const res = await axios.post(`${API}/daily-sales-report/recipients`, {
          name: recipientForm.name.trim(), mobile, is_active: recipientForm.is_active,
        });
        setRecipients((prev) => [...prev, res.data]);
        toast.success('Recipient added');
      }
      setShowRecipientForm(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSavingRecipient(false); }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/daily-sales-report/recipients/${deleting.id}`);
      setRecipients((prev) => prev.filter((x) => x.id !== deleting.id));
      toast.success('Recipient removed');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDeleting(null); }
  };

  const toggleActive = async (r) => {
    try {
      const res = await axios.put(`${API}/daily-sales-report/recipients/${r.id}`, { is_active: !r.is_active });
      setRecipients((prev) => prev.map((x) => (x.id === res.data.id ? res.data : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const activeRecipientCount = useMemo(
    () => recipients.filter((r) => r.is_active).length, [recipients],
  );

  const yesterdayHasData = preview?.has_yesterday_data;

  return (
    <DashboardLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <SendHorizontal className="w-6 h-6 text-emerald-600" /> Daily Sales Report
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Yesterday&apos;s daily collections + Month-to-Date grand totals delivered to Directors via WhatsApp.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="dsr-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={sendNow}
            disabled={sending || activeRecipientCount === 0 || !yesterdayHasData}
            data-testid="dsr-send-now-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <SendHorizontal className="w-4 h-4 mr-1" />}
            Send Now
          </Button>
        </div>
      </div>

      {loading || !settings || !preview ? (
        <Card className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></Card>
      ) : (
        <Tabs defaultValue="schedule" className="space-y-4" data-testid="dsr-tabs">
          <TabsList className="h-10">
            <TabsTrigger value="schedule" data-testid="dsr-tab-schedule">
              <Clock className="w-4 h-4 mr-1.5" /> Schedule &amp; Template
            </TabsTrigger>
            <TabsTrigger value="recipients" data-testid="dsr-tab-recipients">
              <Users className="w-4 h-4 mr-1.5" /> Recipients
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="dsr-tab-history">
              <History className="w-4 h-4 mr-1.5" /> Send History
            </TabsTrigger>
          </TabsList>

          {/* TAB: Recipients */}
          <TabsContent value="recipients" className="space-y-4">
            <Card className="p-5" data-testid="dsr-recipients-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-600" /> Recipients
                  <span className="text-xs text-slate-500 font-normal">({activeRecipientCount} active / {recipients.length} total)</span>
                </h3>
                <Button size="sm" variant="outline" onClick={openNewRecipient} data-testid="dsr-add-recipient-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
              {recipients.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-8">
                  No recipients yet. Add directors here so they receive the WhatsApp report.
                </div>
              ) : (
                <div className="border rounded-md divide-y">
                  {recipients.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 px-3 py-2" data-testid={`dsr-recipient-row-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{r.name}</div>
                        <div className="text-xs text-slate-500 font-mono">+91 {r.mobile}</div>
                      </div>
                      <Switch checked={r.is_active} onCheckedChange={() => toggleActive(r)} data-testid={`dsr-recipient-active-${r.id}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditRecipient(r)} data-testid={`dsr-recipient-edit-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setDeleting(r)} data-testid={`dsr-recipient-delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* TAB: Schedule & Template */}
          <TabsContent value="schedule" className="space-y-4">
            {/* Preview */}
            <Card className="p-5" data-testid="dsr-preview-card">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-bold text-slate-800 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-emerald-600" /> Report Preview</h3>
                <span className="text-xs text-slate-500">for {preview.yesterday}</span>
              </div>

              {!yesterdayHasData && (
                <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2.5 py-1.5 mb-3" data-testid="dsr-no-data-warn">
                  <Ban className="w-3.5 h-3.5" />
                  No Day Book entries for {preview.yesterday} — the report will not be sent until data is filed.
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Daily (excl. bulk)</div>
                  <div className="space-y-0.5">
                    {VENUE_CODES.map((c) => (
                      <div key={c} className="flex justify-between border-b py-0.5" data-testid={`dsr-daily-${c}`}>
                        <span className="font-mono text-xs">{c}</span>
                        <span className="font-semibold text-slate-800">₹{(preview.daily[c] || 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">MTD (incl. bulk)</div>
                  <div className="space-y-0.5">
                    {VENUE_CODES.map((c) => (
                      <div key={c} className="flex justify-between border-b py-0.5" data-testid={`dsr-mtd-${c}`}>
                        <span className="font-mono text-xs">{c}</span>
                        <span className="font-semibold text-emerald-700">₹{(preview.mtd[c] || 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-xs text-slate-500">Rendered WhatsApp Message</Label>
                <Textarea readOnly rows={12} value={preview.preview_message} className="font-mono text-xs bg-slate-50" data-testid="dsr-preview-text" />
                <div className="text-[11px] text-slate-500 mt-1">
                  Template <b>{preview.template_name}</b> · params:
                  <span className="ml-1 font-mono">{'{{'}1{'}}'}…{'{{'}7{'}}'}</span>
                </div>
              </div>

              {/* Venue-wise breakdown for admin reference */}
              <div className="mt-5 border-t pt-4" data-testid="dsr-breakdown-section">
                <div className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                  Admin Reference — Venue-wise Calculation Breakdown
                </div>

                {/* MTD Breakdown */}
                <div className="mb-3">
                  <div className="text-[11px] text-emerald-700 font-semibold mb-1">
                    MTD ({preview.month_start} → {preview.yesterday})
                  </div>
                  <table className="w-full text-xs border rounded-md overflow-hidden">
                    <thead>
                      <tr className="bg-emerald-50 text-emerald-800">
                        <th className="text-left px-2 py-1.5 font-semibold">Venue</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Day Book</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Bulk Booking</th>
                        <th className="text-right px-2 py-1.5 font-semibold border-l border-emerald-200">Total MTD</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {VENUE_CODES.map((c) => {
                        const db  = preview.daybook_mtd?.[c] || 0;
                        const bk  = preview.bulk_mtd?.[c] || 0;
                        const tot = preview.mtd?.[c] || 0;
                        return (
                          <tr key={c} className="bg-white hover:bg-emerald-50/40">
                            <td className="px-2 py-1.5 font-mono font-semibold text-slate-700">{c}</td>
                            <td className="px-2 py-1.5 text-right text-slate-600">₹{db.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right text-blue-700 font-medium">₹{bk.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right font-bold text-emerald-700 border-l border-emerald-100">₹{tot.toLocaleString('en-IN')}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-emerald-50/60 font-semibold">
                        <td className="px-2 py-1.5 text-slate-700">Total</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">
                          ₹{VENUE_CODES.reduce((s, c) => s + (preview.daybook_mtd?.[c] || 0), 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-2 py-1.5 text-right text-blue-700">
                          ₹{VENUE_CODES.reduce((s, c) => s + (preview.bulk_mtd?.[c] || 0), 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-2 py-1.5 text-right text-emerald-800 border-l border-emerald-100">
                          ₹{VENUE_CODES.reduce((s, c) => s + (preview.mtd?.[c] || 0), 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Last Month Breakdown */}
                <div>
                  <div className="text-[11px] text-violet-700 font-semibold mb-1">
                    Last Month — Day Book: {preview.last_month_start} → {preview.last_month_end} (same period) | Bulk: full month
                  </div>
                  <table className="w-full text-xs border rounded-md overflow-hidden">
                    <thead>
                      <tr className="bg-violet-50 text-violet-800">
                        <th className="text-left px-2 py-1.5 font-semibold">Venue</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Day Book</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Bulk Booking</th>
                        <th className="text-right px-2 py-1.5 font-semibold border-l border-violet-200">Total LM</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {VENUE_CODES.map((c) => {
                        const db  = preview.daybook_lm?.[c] || 0;
                        const bk  = preview.bulk_lm?.[c] || 0;
                        const tot = preview.last_month?.[c] || 0;
                        return (
                          <tr key={c} className="bg-white hover:bg-violet-50/40">
                            <td className="px-2 py-1.5 font-mono font-semibold text-slate-700">{c}</td>
                            <td className="px-2 py-1.5 text-right text-slate-600">₹{db.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right text-blue-700 font-medium">₹{bk.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1.5 text-right font-bold text-violet-700 border-l border-violet-100">₹{tot.toLocaleString('en-IN')}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-violet-50/60 font-semibold">
                        <td className="px-2 py-1.5 text-slate-700">Total</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">
                          ₹{VENUE_CODES.reduce((s, c) => s + (preview.daybook_lm?.[c] || 0), 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-2 py-1.5 text-right text-blue-700">
                          ₹{VENUE_CODES.reduce((s, c) => s + (preview.bulk_lm?.[c] || 0), 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-2 py-1.5 text-right text-violet-800 border-l border-violet-100">
                          ₹{VENUE_CODES.reduce((s, c) => s + (preview.last_month?.[c] || 0), 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

            {/* Settings */}
            <Card className="p-5" data-testid="dsr-settings-card">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-600" /> Schedule &amp; Template</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">WABA Template Name</Label>
                  <Input value={settings.template_name} onChange={(e) => patchSettings({ template_name: e.target.value })} data-testid="dsr-template-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Send Time (HH:MM, 24h)</Label>
                  <Input type="time" value={settings.send_time} onChange={(e) => patchSettings({ send_time: e.target.value })} data-testid="dsr-send-time" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Time Zone</Label>
                  <Select value={settings.timezone} onValueChange={(v) => patchSettings({ timezone: v })}>
                    <SelectTrigger data-testid="dsr-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(TZ_OPTIONS.includes(settings.timezone) ? TZ_OPTIONS : [settings.timezone, ...TZ_OPTIONS])
                        .map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between col-span-2 border rounded-md p-2.5">
                  <div>
                    <div className="text-sm font-medium">Auto-send daily</div>
                    <div className="text-xs text-slate-500">
                      {settings.enabled
                        ? `Scheduler will send at ${settings.send_time} ${settings.timezone} every day.`
                        : 'Turn on to auto-send at the configured time daily.'}
                    </div>
                  </div>
                  <Switch checked={!!settings.enabled} onCheckedChange={(v) => patchSettings({ enabled: v })} data-testid="dsr-enabled-switch" />
                </div>

                {/* Venue mapping */}
                <div className="col-span-2 pt-2 border-t">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Venue Mapping (placeholder → venue)</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {VENUE_CODES.map((code) => (
                      <div key={code} className="space-y-1">
                        <Label className="text-[11px] font-mono">{code}</Label>
                        <Select value={settings.venue_map?.[code] || ''} onValueChange={(v) => patchVenueMap(code, v)}>
                          <SelectTrigger data-testid={`dsr-venue-${code}`}><SelectValue placeholder="Select venue" /></SelectTrigger>
                          <SelectContent>
                            {(venueNames.length ? venueNames : [settings.venue_map?.[code]].filter(Boolean))
                              .map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-3">
                <Button onClick={saveSettings} disabled={savingSettings} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dsr-save-settings">
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Settings
                </Button>
              </div>
              {settings.updated_at && (
                <div className="text-[11px] text-slate-400 mt-2">
                  Last updated {fmtDateTime(settings.updated_at)} by {settings.updated_by || 'admin'}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* TAB: Send History */}
          <TabsContent value="history">
            <Card className="p-5" data-testid="dsr-history-card">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <History className="w-4 h-4 text-emerald-600" /> Send History
              </h3>
              {history.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-8">No reports sent yet.</div>
              ) : (
                <div className="border rounded-md divide-y max-h-[440px] overflow-y-auto">
                  {history.map((h) => (
                    <div key={h.id} className="px-3 py-2 text-sm" data-testid={`dsr-history-${h.id}`}>
                      <button className="w-full flex items-center gap-2 text-left" onClick={() => setExpandedLog(expandedLog === h.id ? null : h.id)}>
                        {h.status === 'success'
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-slate-500">{fmtDateTime(h.sent_at)}</div>
                          <div className="font-medium">
                            {h.report_date} <span className="text-slate-400">·</span> {h.success_count} sent / {h.failure_count} failed
                            {h.forced && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">FORCED</span>}
                            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{h.trigger}</span>
                          </div>
                        </div>
                      </button>
                      {expandedLog === h.id && (
                        <div className="mt-2 space-y-1 pl-6">
                          <div className="text-xs text-slate-500">Yesterday {h.yesterday} · Template <b>{h.template_name}</b></div>
                          {(h.results || []).map((res, i) => (
                            <div key={i} className="text-xs flex items-center gap-1.5">
                              {res.success ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <XCircle className="w-3 h-3 text-red-500" />}
                              <span className="font-mono">{res.mobile}</span>
                              <span className="text-slate-500 truncate">{res.name}</span>
                              {!res.success && res.error && <span className="text-red-500 truncate">— {res.error}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Recipient dialog */}
      <Dialog open={showRecipientForm} onOpenChange={setShowRecipientForm}>
        <DialogContent className="sm:max-w-[420px]" data-testid="dsr-recipient-dialog">
          <DialogHeader>
            <DialogTitle>{editingRecipient ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle>
            <DialogDescription className="sr-only">Manage a Daily Sales Report recipient</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={recipientForm.name} onChange={(e) => setRecipientForm({ ...recipientForm, name: e.target.value })} data-testid="dsr-recipient-name" autoFocus />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Mobile (10-digit Indian number)</Label>
              <Input value={recipientForm.mobile} onChange={(e) => setRecipientForm({ ...recipientForm, mobile: e.target.value })} inputMode="numeric" placeholder="98xxxxxxxx" data-testid="dsr-recipient-mobile" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={recipientForm.is_active} onCheckedChange={(v) => setRecipientForm({ ...recipientForm, is_active: v })} data-testid="dsr-recipient-active-switch" />
              Active (receives reports)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecipientForm(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={saveRecipient} disabled={savingRecipient} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dsr-recipient-save">
              {savingRecipient ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="sm:max-w-[380px]" data-testid="dsr-delete-dialog">
          <DialogHeader><DialogTitle>Remove Recipient</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Remove <b>{deleting?.name}</b> ({deleting?.mobile}) from the daily report?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doDelete} data-testid="dsr-delete-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
